/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

const ANIMATION_SESSION_KEY = 'liveSearchSliderAnimationCount'
const MAX_SESSION_ANIMATIONS_PER_SESSION = 2
const ANIMATION_CLASS = 'live-search-slider-peek'
const DEFAULT_ANIMATION_CONFIG = {
    delay: 900,
    fadeIn: 280,
    hold: 180,
    fadeOut: 280
}

const liveSearchProductsAnimation = {
    sliderAnimationTimeoutId: null,
    sliderAnimationScheduled: false,
    sliderAnimationPlayed: false,
    sliderAnimationPending: false,
    sliderAnimationObserver: null,
    sliderAnimationObservedTarget: null,

    /**
     * Schedule the slider animation when the product list finished loading
     *
     * @param {CustomEvent} event
     */
    triggerSliderAnimation(event) {
        if (!this.shouldScheduleSliderAnimation(event)) {
            return
        }

        this.sliderAnimationScheduled = true
        this.sliderAnimationPending = true
        const delay = this.getSliderAnimationConfig().delay

        if (this.sliderAnimationTimeoutId) {
            window.clearTimeout(this.sliderAnimationTimeoutId)
        }

        this.sliderAnimationTimeoutId = window.setTimeout(() => {
            this.sliderAnimationTimeoutId = null
            this.sliderAnimationScheduled = false
            this.startSliderAnimation()
        }, delay)
    },

    /**
     * Check if an animation should be scheduled
     *
     * @param {CustomEvent} event
     * @returns {boolean}
     * @private
     */
    shouldScheduleSliderAnimation(event) {
        if (!event?.detail || event.detail.type !== 'initial') {
            return false
        }

        if (this.sliderAnimationScheduled || this.sliderAnimationPlayed) {
            return false
        }

        if (!this.isTouchDevice()) {
            return false
        }

        if (this.getAnimationSessionCount() >= MAX_SESSION_ANIMATIONS_PER_SESSION) {
            return false
        }

        return Array.isArray(this.products) && this.products.length > 0
    },

    /**
     * Execute the animation on the first available slider
     */
    startSliderAnimation() {
        if (!this.sliderAnimationPending || this.sliderAnimationPlayed) {
            return
        }

        const targetProduct = this.getAnimationTargetProduct()
        const sliderTrack = this.findSliderTrack(targetProduct)

        if (!sliderTrack) {
            return
        }

        this.startSliderAnimationForTrack(sliderTrack)
    },
    /**
     * Try to start the animation when a product slider becomes visible
     *
     * @param {string} productKey
     */
    tryStartSliderAnimation(productKey) {
        if (!this.sliderAnimationPending || this.sliderAnimationPlayed) {
            return
        }

        if (this.sliderAnimationScheduled) {
            return
        }

        if (!this.isTouchDevice()) {
            return
        }

        if (this.getAnimationSessionCount() >= MAX_SESSION_ANIMATIONS_PER_SESSION) {
            this.sliderAnimationPending = false
            return
        }

        const product = Array.isArray(this.products)
            ? this.products.find((entry) => String(entry?.instanceKey) === String(productKey))
            : null

        if (!this.isSliderCandidate(product)) {
            return
        }

        const sliderTrack = this.findSliderTrack(product)
        if (!sliderTrack) {
            return
        }

        this.startSliderAnimationForTrack(sliderTrack)
    },
    /**
     * Start the animation or observe the slider until it becomes visible
     *
     * @param {HTMLElement} sliderTrack
     */
    startSliderAnimationForTrack(sliderTrack) {
        if (!sliderTrack) {
            return
        }

        if (!this.isElementInViewport(sliderTrack)) {
            this.observeSliderForAnimation(sliderTrack)
            return
        }

        this.cleanupSliderAnimationObserver()
        this.sliderAnimationPending = false
        this.sliderAnimationPlayed = true
        this.incrementAnimationSessionCount()
        this.applySliderAnimation(sliderTrack)
    },
    /**
     * Observe a slider until it becomes visible
     *
     * @param {HTMLElement} sliderTrack
     */
    observeSliderForAnimation(sliderTrack) {
        if (typeof IntersectionObserver !== 'function') {
            return
        }

        if (this.sliderAnimationObservedTarget === sliderTrack && this.sliderAnimationObserver) {
            return
        }

        this.cleanupSliderAnimationObserver()
        this.sliderAnimationObservedTarget = sliderTrack
        this.sliderAnimationObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return
                }

                this.cleanupSliderAnimationObserver()
                if (this.sliderAnimationPlayed || !this.sliderAnimationPending) {
                    return
                }

                this.sliderAnimationPending = false
                this.sliderAnimationPlayed = true
                this.incrementAnimationSessionCount()
                this.applySliderAnimation(entry.target)
            })
        }, { root: null, threshold: 0.5 })

        this.sliderAnimationObserver.observe(sliderTrack)
    },
    /**
     * Disconnect any pending slider animation observer
     */
    cleanupSliderAnimationObserver() {
        if (this.sliderAnimationObserver) {
            this.sliderAnimationObserver.disconnect()
            this.sliderAnimationObserver = null
            this.sliderAnimationObservedTarget = null
        }
    },

    /**
     * Resolve the first product that has a slider, fallback to the first product
     *
     * @returns {Object|null}
     */
    getAnimationTargetProduct() {
        if (!Array.isArray(this.products) || this.products.length === 0) {
            return null
        }

        const productWithSlider = this.products.find((product) => this.isSliderCandidate(product))

        return productWithSlider || this.products[0]
    },
    /**
     * Check if a product has a slider with multiple images
     *
     * @param {Object|null} product
     * @returns {boolean}
     */
    isSliderCandidate(product) {
        return Boolean(product) &&
            Array.isArray(product?.images?.slides) &&
            product.images.slides.length > 1
    },
    /**
     * Check if an element is visible in the viewport
     *
     * @param {HTMLElement} element
     * @returns {boolean}
     */
    isElementInViewport(element) {
        if (!element || typeof element.getBoundingClientRect !== 'function') {
            return false
        }

        const rect = element.getBoundingClientRect()
        return rect.bottom > 0 &&
            rect.right > 0 &&
            rect.top < (window.innerHeight || 0) &&
            rect.left < (window.innerWidth || 0)
    },

    /**
     * Find the slider track element for the given product
     *
     * @param {Object|null} product
     * @returns {HTMLElement|null}
     */
    findSliderTrack(product) {
        if (typeof document === 'undefined' || !product) {
            return null
        }

        const productList = document.getElementById('product-list')
        const baseSelector = '.product-image-photos [data-slider-track]'
        const selector = product?.instanceKey
            ? `[data-product-instance-key="${this.escapeSelector(String(product.instanceKey))}"] ${baseSelector}`
            : baseSelector

        return productList?.querySelector(selector) || document.querySelector(selector)
    },

    /**
     * Escape selector values when CSS.escape is not available
     *
     * @param {string} value
     * @returns {string}
     */
    escapeSelector(value) {
        if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
            return CSS.escape(value)
        }

        return value.replace(/([:\\[\\]\\.#])/g, '\\$1')
    },

    /**
     * Resolve the scroll container for a slider track.
     *
     * @param {HTMLElement|null} sliderTrack
     * @returns {HTMLElement|null}
     */
    getSliderScrollContainer(sliderTrack) {
        if (!sliderTrack) {
            return null
        }

        if (typeof sliderTrack.closest === 'function') {
            const container = sliderTrack.closest('[x-ref="sliderTrack"]')
            if (container) {
                return container
            }
        }

        return sliderTrack.parentElement || null
    },

    /**
     * Apply the animation class with configured timing variables
     *
     * @param {HTMLElement} sliderTrack
     */
    applySliderAnimation(sliderTrack) {
        const { delay, fadeIn, hold, fadeOut } = this.getSliderAnimationConfig()
        const scrollContainer = this.getSliderScrollContainer(sliderTrack)
        const resetScroll = () => {
            if (!scrollContainer) {
                return
            }

            if (typeof scrollContainer.scrollTo === 'function') {
                scrollContainer.scrollTo({ left: 0 })
            }

            scrollContainer.scrollLeft = 0
        }

        sliderTrack.style.setProperty('--live-search-slider-peek-delay', `${delay}ms`)
        sliderTrack.style.setProperty('--live-search-slider-peek-fade-in', `${fadeIn}ms`)
        sliderTrack.style.setProperty('--live-search-slider-peek-hold', `${hold}ms`)
        sliderTrack.style.setProperty('--live-search-slider-peek-fade-out', `${fadeOut}ms`)
        sliderTrack.style.setProperty('--live-search-slider-peek-distance', '15%')

        resetScroll()
        sliderTrack.classList.remove(ANIMATION_CLASS)
        // Force reflow to restart the animation if the class was present

        sliderTrack.offsetWidth
        sliderTrack.classList.add(ANIMATION_CLASS)

        const cleanup = (event) => {
            if (!event || event.animationName !== 'live-search-slider-peek-out') {
                return
            }

            sliderTrack.classList.remove(ANIMATION_CLASS)
            sliderTrack.removeEventListener('animationend', cleanup)
            resetScroll()
            if (typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(resetScroll)
            }
        }

        sliderTrack.addEventListener('animationend', cleanup)
    },

    /**
     * Normalize animation settings from the window config
     *
     * @returns {{delay: number, fadeIn: number, hold: number, fadeOut: number}}
     */
    getSliderAnimationConfig() {
        const config = window.liveSearchConfig?.sliderAnimation || {}

        return {
            delay: this.normalizeDuration(config.delay, DEFAULT_ANIMATION_CONFIG.delay),
            fadeIn: this.normalizeDuration(config.fadeIn, DEFAULT_ANIMATION_CONFIG.fadeIn),
            hold: this.normalizeDuration(config.hold, DEFAULT_ANIMATION_CONFIG.hold),
            fadeOut: this.normalizeDuration(config.fadeOut, DEFAULT_ANIMATION_CONFIG.fadeOut)
        }
    },

    /**
     * Normalize a duration to a non-negative integer with fallback
     *
     * @param {number|string} value
     * @param {number} fallback
     * @returns {number}
     */
    normalizeDuration(value, fallback) {
        const duration = Number(value)

        if (Number.isFinite(duration) && duration >= 0) {
            return duration
        }

        return fallback
    },

    /**
     * Get the animation play count from session storage
     *
     * @returns {number}
     */
    getAnimationSessionCount() {
        try {
            const stored = window.sessionStorage.getItem(ANIMATION_SESSION_KEY)
            const parsed = parseInt(stored ?? '0', 10)

            return Number.isFinite(parsed) ? parsed : 0
        } catch (error) {
            return 0
        }
    },

    /**
     * Increment the animation play count in session storage
     */
    incrementAnimationSessionCount() {
        const nextValue = this.getAnimationSessionCount() + 1

        try {
            window.sessionStorage.setItem(ANIMATION_SESSION_KEY, String(nextValue))
        } catch (error) {
            // Ignore storage errors (private browsing, disabled storage, etc.)
        }
    }
}

export default liveSearchProductsAnimation
